import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import {
  ACTIVE_RUNS_REFRESH_EVENT,
  getRunLifecycleStatus,
  isActiveLifecycleStatus,
} from '../utils/activeRunsEvents';

interface ActiveRunSnapshot {
  requestId: string;
  status: string;
  videoName?: string;
  message?: string;
  progress?: number;
  chunks_processed?: number;
  total_chunks?: number;
  analysis_tier?: string;
  display_category_total?: number;
  processing_time_seconds?: number;
  llm_video_prep?: {
    optimized?: boolean;
    skip_reason?: string;
  };
}

interface ActiveRunsCountResult {
  activeCount: number;
  processingCount: number;
  pendingCount: number;
  activeRun: ActiveRunSnapshot | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  lastUpdated: Date | null;
}

// When runs are active, poll every 5 seconds for real-time updates
const ACTIVE_REFRESH_INTERVAL = 5000;
// When idle, poll less frequently to save resources
const IDLE_REFRESH_INTERVAL = 60000;
// After starting a run, poll quickly until the server reflects it (or timeout)
const POST_START_BOOST_MS = 120_000;

export const useActiveRunsCount = (refreshInterval?: number): ActiveRunsCountResult => {
  const { currentUser } = useAuth();
  const [activeCount, setActiveCount] = useState<number>(0);
  const [processingCount, setProcessingCount] = useState<number>(0);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [activeRun, setActiveRun] = useState<ActiveRunSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [pollBoostUntil, setPollBoostUntil] = useState<number | null>(null);

  const fetchActiveRunsCount = useCallback(async () => {
    const userId = currentUser?.uid;
    if (!userId) {
      setActiveCount(0);
      setProcessingCount(0);
      setPendingCount(0);
      setActiveRun(null);
      return;
    }

    try {
      setIsLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/api/v1/user/${userId}/active-runs`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(8000) // 8 second timeout
      });
      
      if (response.ok) {
        const data = await response.json();

        const runs: any[] = data.active_runs && Array.isArray(data.active_runs)
          ? data.active_runs
          : data.runs && Array.isArray(data.runs)
            ? data.runs
            : [];

        if (runs.length > 0) {
          const activeRuns = runs.filter((run: any) =>
            isActiveLifecycleStatus(getRunLifecycleStatus(run))
          );
          const processing = activeRuns.filter((run: any) => {
            const lifecycle = getRunLifecycleStatus(run);
            return lifecycle === 'processing' || run.status === 'running';
          }).length;
          const pending = activeRuns.filter((run: any) => {
            const lifecycle = getRunLifecycleStatus(run);
            return lifecycle === 'pending' || lifecycle === 'queued';
          }).length;

          setActiveCount(activeRuns.length);
          setProcessingCount(processing);
          setPendingCount(pending);
          const firstRun = activeRuns[0];
          setActiveRun(firstRun ? {
            requestId: firstRun.request_id || firstRun.requestId || '',
            status: getRunLifecycleStatus(firstRun),
            videoName: firstRun.video_name || firstRun.videoName || firstRun.url,
            message: firstRun.message,
            progress: firstRun.progress,
            chunks_processed: firstRun.chunks_processed,
            total_chunks: firstRun.total_chunks,
            analysis_tier: firstRun.analysis_tier,
            display_category_total: firstRun.display_category_total,
            processing_time_seconds: firstRun.processing_time_seconds,
            llm_video_prep: firstRun.llm_video_prep,
          } : null);
        } else {
          setActiveCount(0);
          setProcessingCount(0);
          setPendingCount(0);
          setActiveRun(null);
        }
        setError(null);
        setLastUpdated(new Date());
      } else if (response.status === 404) {
        // No active runs found - this is normal
        setActiveCount(0);
        setProcessingCount(0);
        setPendingCount(0);
        setActiveRun(null);
        setError(null);
        setLastUpdated(new Date());
      } else {
        throw new Error(`API error: ${response.status}`);
      }
      
    } catch (err) {
      // Silently handle connection errors to prevent spam
      if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        setError('Connection error');
      } else if (err instanceof Error && err.name === 'AbortError') {
        setError('Timeout');
      } else {
        setError('Failed to fetch');
      }
      // Don't reset counts on error to avoid flicker
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.uid]);

  // Initial fetch
  useEffect(() => {
    if (currentUser?.uid) {
      fetchActiveRunsCount();
    }
  }, [currentUser?.uid, fetchActiveRunsCount]);

  // Immediate refresh when a new run is submitted anywhere in the app
  useEffect(() => {
    const onRunStarted = () => {
      setPollBoostUntil(Date.now() + POST_START_BOOST_MS);
      void fetchActiveRunsCount();
    };
    window.addEventListener(ACTIVE_RUNS_REFRESH_EVENT, onRunStarted);
    return () => window.removeEventListener(ACTIVE_RUNS_REFRESH_EVENT, onRunStarted);
  }, [fetchActiveRunsCount]);

  // Polling effect with dynamic interval based on active runs
  useEffect(() => {
    if (!currentUser?.uid) return;

    const boostActive = pollBoostUntil != null && Date.now() < pollBoostUntil;
    const effectiveInterval = refreshInterval ?? (
      activeCount > 0 || boostActive ? ACTIVE_REFRESH_INTERVAL : IDLE_REFRESH_INTERVAL
    );

    const interval = setInterval(() => {
      fetchActiveRunsCount();
    }, effectiveInterval);

    return () => clearInterval(interval);
  }, [currentUser?.uid, refreshInterval, fetchActiveRunsCount, activeCount, pollBoostUntil]);

  // Manual refresh function
  const refresh = useCallback(() => {
    fetchActiveRunsCount();
  }, [fetchActiveRunsCount]);

  return {
    activeCount,
    processingCount,
    pendingCount,
    activeRun,
    isLoading,
    error,
    refresh,
    lastUpdated
  };
};
